import { Component, OnInit, OnDestroy } from '@angular/core';
import { Tracking, TrackingAggregato } from '../../services/business-data.service';
import { TrackingParcel, TrackingWaybill } from '../../services/data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';

interface RoutingPoint {
  warehouseCode: string | null;
  titolo: string | null;
  status: string;
  data: string;
  ora: string;
  dettagli: DettaglioEvento[];
}

interface DettaglioEvento {
  iconClass: string;
  collo: string;
  descrizione?: string;
  evento: string;
  data: string;
  ora: string;
}

export interface Dealer {
  dealerId: number;                  
  dealerCode: string | null;
  dealerName: string | null;
  dealerVatNumber: string | null;
  dealerInternalBillingId: string | null;
  dealerInternalId: string | null;
  dealerSdiCode: string | null;
  dealerDeliveryZoneCode: string | null;
  dealerBillingZoneCode: string | null;
  dealerLegalAddressStreet: string | null;
  dealerLegalAddressNumber: string | null;
  dealerLegalAddressZip: string | null;
  dealerLegalAddressCity: string | null;
  dealerLegalAddressProvince: string | null;
  dealerLegalAddressCountry: string | null;
  dealerOperatingAddressStreet: string | null;
  dealerOperatingAddressNumber: string | null;
  dealerOperatingAddressZip: string | null;
  dealerOperatingAddressCity: string | null;
  dealerOperatingAddressProvince: string | null;
  dealerOperatingAddressCountry: string | null;
  dealerIncorporationDate: string | null;  
  dealerIndustry: string | null;
  dealerStatus: string | null;
  dealerNotes: string | null;
  dealerLatitude: number | null;             
  dealerLongitude: number | null;            
  dealerVersion: number | null;              
  dealerCreatedAt: string | null;             
}

export interface Warehouse {
  warehouseId: number; 
  warehouseCode: string;
  warehouseName?: string;
  warehouseShortName?: string;
  warehouseLocationId?: string;
  warehouseAddressStreet?: string;
  warehouseAddressNumber?: string;
  warehouseAddressZip?: string;
  warehouseAddressCity?: string;
  warehouseAddressProvince?: string;
  warehouseAddressCountry?: string;
  warehouseCapacity?: number;
  warehouseContactPerson?: string;
  warehouseContactPhone?: string;
  warehouseLatitude?: number;
  warehouseLongitude?: number;
  warehouseType?: string;
  warehouseStatus?: string;
  warehouseVersion?: number;
  warehouseAffiliateCode?: string;
  warehouseAffiliateType?: string;
  warehouseUpdatedAt?: string; 
  warehouseUpdatedBy?: string;
  warehouseCreatedAt?: string; 
}

export interface Client {
  clientId: number; 
  clientCode: string;
  clientName?: string;
}

@Component({
  selector: 'app-trackingDetails',
  standalone: true,
  templateUrl: './trackingDetails.component.html',
  styleUrls: ['./trackingDetails.component.css'],
  imports: [FormsModule, CommonModule, TranslateModule]
})
export class TrackingDetailsComponent implements OnInit, OnDestroy {

  constructor(private route: ActivatedRoute, private router: Router, private http: HttpClient) {}
  trackings: Tracking[] = [];
  waybills: TrackingWaybill[] = [];
  parcels: TrackingParcel[] = [];
  dealers: Dealer[] = [];
  warehouses: Warehouse[] = [];
  allWarehouses: Warehouse[] = [];
  client: Client | null = null;
  trackingCode: string = '';
  btnResetVisible: boolean = false;
  notFound: boolean = false;
  loadError: boolean = false;

  ngOnInit(): void {
    this.startCountdown();
      // Leggi il parametro 'code' dalla rotta
      this.route.paramMap.subscribe((params) => {
        const code = params.get('code');
        if (code) {
          this.trackingCode = code;
          this.loadTrackingData(code); // Carica i dati di tracking
        }
      });
    }
  
    // Funzione per caricare i dati di tracking
  
    loadTrackingData(code: string): void {
        forkJoin({
        trackings: this.http.get<Tracking[]>(`${environment.apiUrl}/tracking/${code}`),
        parcels: this.http.get<TrackingParcel[]>(`${environment.apiUrl}/tracking/parcelTracking/${code}`),
        waybills: this.http.get<TrackingWaybill[]>(`${environment.apiUrl}/tracking/waybill/${code}`),
        dealers: this.http.get<Dealer[]>(`${environment.apiUrl}/tracking/dealer/${code}`),
        warehouses: this.http.get<Warehouse[]>(`${environment.apiUrl}/tracking/warehouse/${code}`),
        allWarehouses: this.http.get<Warehouse[]>(`${environment.apiUrl}/tracking/warehouses`),
        clients: this.http.get<Client[]>(`${environment.apiUrl}/tracking/client/${code}`)
      }).subscribe({
        next: ({ trackings, parcels, waybills, dealers, warehouses, allWarehouses, clients }) => {
          if (!waybills || waybills.length === 0) {
            this.notFound = true;
            return;
          }
          this.trackings = trackings;
          const uniqueParcels = parcels.filter(
            (p, index, self) =>
              index === self.findIndex(other => other.parcelCode === p.parcelCode)
          );
          this.parcels = uniqueParcels;
          this.waybills = waybills;
          this.dealers = dealers;
          this.warehouses = warehouses;
          this.allWarehouses = allWarehouses;
          this.client = clients.length > 0 ? clients[0] : null;
          this.buildRoutingPoints();
        },
        error: (err) => {
          console.error('Errore durante le chiamate GET:', err);
          this.loadError = true;
        }
      });
  }

  selectedPoint: {
  warehouseCode: string;
  titolo: string;
  status: string;
  data: string;
  ora: string;
  dettagli: {
    collo: string;
    descrizione?: string;
    evento: string;
    data: string;
    ora: string;
    iconClass?: string;
  }[];
} | null = null;

  getTotalWeight(): number {
    return this.parcels.reduce((sum, p) => sum + (p.parcelWeight || 0), 0);
  }
  getTotalVolume(): string {
    return this.parcels.reduce((sum, p) => sum + (p.parcelVolume || 0), 0).toFixed(3);
  }
  getNumeroColli(): number {
    return this.parcels.length;
  }

  getParcelType(): string {
    if(this.parcels.length > 0) {
      if(this.parcels[0].parcelType == 'S')
        return 'Consegna stock';
      else
        return 'Consegna ugente';
    }
    else
      return '';
  }

  // shipmentDetails: any = {
  //   id: '12345',
  //   mittente: 'Mario Rossi',
  //   destinatario: 'Luigi Bianchi',
  //   indirizzo: 'Via Roma 10, Milano',
  //   note: 'Fragile, maneggiare con cura',
  // };

  // parcelDetails: any = {
  //   pesoTotale: '12 kg',
  //   volumeTotale: '0.5 m³',
  //   numeroColli: 3,
  //   dimensioni: '50x40x30 cm',
  //   contenuto: 'Elettronica',
  // };

  // routingPoints: RoutingPoint[] = [
  //   {
  //     titolo: 'Centro Smistamento Milano',
  //     status: 'positive',
  //     data: '2025-06-08',
  //     ora: '09:00',
  //     dettagli: [
  //       {
  //         iconClass: 'fa-solid fa-circle-check',
  //         collo: '001',
  //         evento: 'Ricezione pacco',
  //         data: '2025-06-08',
  //         ora: '09:00',
  //       },
  //       {
  //         iconClass: 'fa-solid fa-circle-check',
  //         collo: '002',
  //         evento: 'Ricezione pacco',
  //         data: '2025-06-08',
  //         ora: '09:05',
  //       },
  //     ],
  //   },
  //   {
  //     titolo: 'Centro Smistamento Bologna',
  //     status: 'pending',
  //     data: '2025-06-09',
  //     ora: '12:30',
  //     dettagli: [
  //       {
  //         iconClass: 'fa-regular fa-clock',
  //         collo: '001',
  //         evento: 'In attesa',
  //         data: '2025-06-09',
  //         ora: '12:30',
  //       },
  //     ],
  //   },
  // ];

  showDetails = false;

  error: string | null = null;

  countdown: number = 60;
  timerId: any;
  isPaused: boolean = false;

  showResetButton = false;


  ngOnDestroy(): void {
    this.clearTimer();
  }

  startCountdown() {
    this.clearTimer();
    this.timerId = setInterval(() => {
      if (!this.isPaused) {
        this.countdown--;
        if (this.countdown <= 0) {
          this.refreshData();
          this.countdown = 60;
        }
      }
    }, 1000);
  }

  clearTimer() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
  }

  toggleTimer() {
    this.isPaused = !this.isPaused;
  }

  refreshData() {
    // Placeholder per aggiornare i dati dal backend o altro
    window.location.reload();
  }

  showPointDetails(point: any): void {
    this.showDetails = true;
    this.btnResetVisible = true;
    let dettagli: any;
    let stato: string = "";
    let icon: string = "";
    let dataCollo:string | undefined = "";
    
    if(this.trackings.length == 0) {
      if(this.parcels.length > 0) {
        dettagli = this.parcels.map((parcel) => {
          if(point.status == 'positive_request') {
            dataCollo = parcel?.parcelDate;
            if (parcel?.parcelDate != null) {
              stato = 'positive_request';
              icon = 'ARRIVAL';
            } else {
              stato = 'pending';
              icon = 'DEPARTURE';
            }
          } else if(point.status == 'delivered' || point.status == 'incomplete_delivered' || point.status == 'pending_delivery') {
            dataCollo = parcel?.parcelDeliveryDate;
            if (parcel?.parcelDeliveryDate != null) {
              stato = 'delivered'; //'Consegna completata';
              icon = 'DELIVERED';
            } else {
              stato = 'pending_delivery'; //'In attesa di consegna';
              icon = 'DEPARTURE';
            }
          } else if(point.status == 'start_request') {
            dataCollo = parcel?.parcelDate;
            if (parcel?.parcelDate != null) {
              stato = 'start_request';
              icon = 'ARRIVAL';
            } else {
              stato = 'pending';
              icon = 'DEPARTURE';
            }
          }
          return {
          iconClass: this.getIconClass(icon),
          collo: parcel.parcelCode,
          descrizione: parcel?.parcelDescription,
          evento: stato,
          data: this.formatData(dataCollo),
          ora: this.formatOra(dataCollo)
        };
      });
    }
    } else {
      dettagli = this.parcels.map((parcel) => {
      const tracking = this.trackings.find(t => parcel.parcelCode === t.trackingParcelCode && (!point.warehouseCode || point.warehouseCode == t.trackingWarehouseCode));
      if(point.status == 'positive_request') {
        dataCollo = parcel?.parcelDate;
        if (parcel?.parcelDate != null) {
          stato = 'positive_request';
          icon = 'ARRIVAL';
        } else {
          stato = 'pending';
          icon = 'DEPARTURE';
        }
      }
      else if(point.status == 'delivered' || point.status == 'incomplete_delivered' || point.status == 'pending_delivery') {
        dataCollo = parcel?.parcelDeliveryDate;
        if (parcel?.parcelDeliveryDate != null) {
          stato = 'delivered'; //'Consegna completata';
          icon = 'DELIVERED';
        } else {
          stato = 'pending_delivery'; //'In attesa di consegna';
          icon = 'DEPARTURE';
        }
      }
      else if(point.status == 'start_request') {
            dataCollo = parcel?.parcelDate;
            if (parcel?.parcelDate != null) {
              stato = 'start_request';
              icon = 'ARRIVAL';
            } else {
              stato = 'pending';
              icon = 'DEPARTURE';
            }
      }
      else {
        dataCollo = tracking?.trackingCreatedAt;
        if (tracking?.trackingParcelCode != null) {
          stato = 'positive';
          icon = 'ARRIVAL';
        } else {
          stato = 'pending';
          icon = 'DEPARTURE';
        }
      }

      return {
        iconClass: this.getIconClass(icon),
        collo: parcel.parcelCode,
        descrizione: parcel?.parcelDescription,
        evento: stato,
        data: this.formatData(dataCollo),
        ora: this.formatOra(dataCollo)
      };
    });
  }

  this.selectedPoint = {
    warehouseCode: point.warehouseCode,
    titolo: point.titolo,
    status: point.status,
    data: point.data,
    ora: point.ora,
    dettagli: dettagli
  };
}

  formatData(timestamp?: string): string {
    if(timestamp != null && timestamp != "")
      return new Date(timestamp).toLocaleDateString();
    return "-";
  }

  formatOra(timestamp?: string): string {
    if(timestamp != null && timestamp != "")
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return "-";
  }

  getIconClass(evento: string): string {
    switch (evento) {
      case 'ARRIVAL': return 'fa-solid fa-box-open';
      case 'DEPARTURE': return 'fa-solid fa-truck';
      case 'DELIVERED': return 'fa-solid fa-circle-check';
      default: return 'fa-solid fa-circle-info';
    }
  }



  mostraDettagliSpedizione() {
    this.btnResetVisible = false;
    this.showDetails = false;
    this.showResetButton = false;
  }

  goBack(): void {
    // Torna alla ricerca tracking mantenendo il contesto pubblico o privato
    this.router.navigate([this.router.url.startsWith('/private/') ? '/private/tracking' : '/tracking']);
  }



getEventName(t: Tracking): string {
  return t.trackingOperationDescription || t.operationType || 'Evento';
}

inferStatus(events: Tracking[]): string {
  const types = events.map(e => e.operationType);
  if (types.includes('ERROR')) return 'error';
  if (types.includes('WAITING')) return 'pending';
  if (types.includes('DELIVERED')) return 'positive';
  return 'incomplete';
}



  routingPoints: RoutingPoint[] = [];

buildRoutingPoints(): void {

  let stato: string = "";
  let icon: string = "";
  const totali = this.parcels.length;
  const conCodice = this.trackings.filter(t => t.trackingParcelCode?.trim()).length;

  if (conCodice === totali) {
    stato = 'positive';
    icon = 'ARRIVAL';
  } else if (conCodice > 0) {
    stato = 'incomplete';
    icon = 'DEPARTURE';
  } else {
    stato = 'pending';
    icon = 'DEPARTURE'
  }


  let statoDelivery: string = "";
  const conDeliveryDate = this.parcels.filter(p => p.parcelDeliveryDate != null).length;

  if (conDeliveryDate === totali && totali > 0) {
    statoDelivery = 'delivered'; //'Consegna completata';
    icon = 'DELIVERED';
  } else if (conDeliveryDate > 0) {
    statoDelivery = 'incomplete_delivered'; //'Consegna incompleta';
    icon = 'DEPARTURE';
  } else {
    statoDelivery = 'pending_delivery'; //'In attesa di consegna';
    icon = 'DEPARTURE';
  }



  let dataPiuVecchiaParcelTimestamp = this.parcels.length === 0 ||
  this.parcels.every(p => !p.parcelDeliveryDate)
  ? ''
  : this.parcels
      .filter(p => p.parcelDeliveryDate)
      .map(p => new Date(p.parcelDeliveryDate))
      .reduce((min, current) => current < min ? current : min)
      .toISOString();

  this.routingPoints = [];

  // Punto iniziale statico
  this.routingPoints.push({
    warehouseCode: this.waybills[0].waybillOriginLocation,
    titolo: this.getWarehouseName(this.waybills[0].waybillOriginLocation),
    status: 'start_request',
    data: this.formatData(this.parcels[0].parcelDate),
    ora: this.formatOra(this.parcels[0].parcelDate),
    dettagli: this.parcels.map(p => {
      return {
        iconClass: this.getIconClass(icon),
        collo: p.parcelBarcode,
        descrizione: p.parcelDescription,
        evento: '',
        data: this.formatData(p.parcelDate),
        ora: this.formatOra(p.parcelDate)
      };
    })
  });

  const warehousesUnici = this.warehouses.filter(
  (w, index, self) =>
    index === self.findIndex(other => other.warehouseCode === w.warehouseCode)
  );
  // Punti per ogni magazzino intermedio
  warehousesUnici.filter(w => w.warehouseCode != this.waybills[0].waybillOriginLocation).forEach(w => {
      let dataPiuVecchiaTimestamp = this.trackings.length === 0 || 
      this.trackings.every(t => !t.trackingCreatedAt) 
      ? '' 
      : this.trackings
          .filter(t => t.trackingCreatedAt && (!t.trackingWarehouseCode || t.trackingWarehouseCode == w.warehouseCode))
          .map(t => new Date(t.trackingCreatedAt))
          .reduce((min, current) => current < min ? current : min)
          .toISOString();

    this.routingPoints.push({
      warehouseCode: w.warehouseCode,
      titolo: w.warehouseName!,
      status: stato,
      data: this.formatData(dataPiuVecchiaTimestamp),
      ora: this.formatOra(dataPiuVecchiaTimestamp),
      dettagli: this.trackings.filter(t => t.trackingWarehouseCode == w.warehouseCode).map(t => {
        const parcel = this.parcels.find(p => p.parcelCode === t.trackingParcelCode);
        return {
          iconClass: this.getIconClass(icon),
          collo: t.trackingParcelCode,
          descrizione: parcel?.parcelDescription,
          evento: this.getEventName(t),
          data: this.formatData(t.trackingCreatedAt),
          ora: this.formatOra(t.trackingCreatedAt)
        };
      })
    });
  });

  // Punto finale statico (dealer)
  this.routingPoints.push({
    warehouseCode: '',
    titolo: this.dealers.map(d => d.dealerName)[0],
    status: statoDelivery,
    data: this.formatData(dataPiuVecchiaParcelTimestamp),
    ora: this.formatOra(dataPiuVecchiaParcelTimestamp),
    dettagli: this.parcels.map(p => {
      return {
        iconClass: this.getIconClass(icon),
        collo: p.parcelCode,
        descrizione: p.parcelDescription,
        evento: '',
        data: this.formatData(p.parcelDeliveryDate),
        ora: this.formatOra(p.parcelDeliveryDate)
      };
    })
  });
}

  getWarehouseName(warehouseCode: string) {
    return this.allWarehouses.filter(w => w.warehouseCode == warehouseCode)[0] ? this.allWarehouses.filter(w => w.warehouseCode == warehouseCode)[0].warehouseName! : '';
  }

  getStatusClass(status: string | undefined): string {
    switch (status) {
      case 'positive':
      case 'positive_request':
      case 'start_request':
      case 'en_route':
      case 'in_warehouse': return 'badge-positive';
      case 'delivered': return 'badge-delivered';
      case 'error':
      case 'en_route_error':
      case 'in_warehouse_error': return 'badge-error';
      case 'incomplete':
      case 'incomplete_delivered': return 'badge-warning';
      default: return 'badge-pending';
    }
  }

  getStatusLabel(status: string | undefined): string {
    switch (status) {
      case 'positive': return 'Completato';
      case 'positive_request':
      case 'start_request': return 'Richiesta spedizione';
      case 'delivered': return 'Consegnato';
      case 'pending': return 'In attesa';
      case 'pending_delivery': return 'In attesa di consegna';
      case 'error': return 'Fuori Rotta';
      case 'en_route': return 'In transito';
      case 'en_route_error': return 'Errore transito';
      case 'in_warehouse': return 'In magazzino';
      case 'in_warehouse_error': return 'Errore magazzino';
      case 'incomplete': return 'Incompleto';
      case 'incomplete_delivered': return 'Consegna Incompleta';
      default: return status || '—';
    }
  }

  getStatusPillClass(status: string | undefined): string {
    switch (status) {
      case 'positive':
      case 'positive_request':
      case 'start_request':
      case 'delivered':
      case 'en_route':
      case 'in_warehouse': return 'success';
      case 'error':
      case 'en_route_error':
      case 'in_warehouse_error': return 'danger';
      case 'incomplete':
      case 'incomplete_delivered': return 'warning';
      default: return 'info';
    }
  }

  getStatusIconClasses(status: string | undefined): string[] {
    let icon: string;
    let color: string;
    switch (status) {
      case 'positive':
      case 'positive_request':
      case 'start_request':
      case 'delivered':
        icon = 'fa-solid fa-circle-check'; color = 'icon-positive'; break;
      case 'en_route':
      case 'in_warehouse':
        icon = 'fa-solid fa-circle-arrow-right'; color = 'icon-positive'; break;
      case 'en_route_error':
      case 'in_warehouse_error':
        icon = 'fa-solid fa-circle-arrow-right'; color = 'icon-error'; break;
      case 'error':
        icon = 'fa-solid fa-circle-xmark'; color = 'icon-error'; break;
      case 'incomplete':
      case 'incomplete_delivered':
        icon = 'fa-solid fa-circle-minus'; color = 'icon-warning'; break;
      default:
        icon = 'fa-solid fa-circle-minus'; color = 'icon-pending'; break;
    }
    return [...icon.split(' '), color];
  }

}
